import { useEffect } from 'react';

import { FormikProps } from 'formik';

import {
  adminGetProfessionalInfo,
  adminPharmaciesInfo,
  adminResetValue,
  AllowancesNames,
  convertToDecimals,
  getPharmaciesInfo,
  getProfessionalMetaInfo,
  IAdminWorkshiftDetails,
  ProfessionalReplacementTypes,
} from '@pharmaplan/common';

import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';
import { useComponentDidUpdate } from '../../useComponentDidUpdate';
import useSelfService from '../../useSelfService';
import useHourlyRates from '../useHourlyRates';

const { weekDayPaidHourlyRate, weekendPaidHourlyRate } = AllowancesNames;

const useExpressBookingHr = (
  formik: FormikProps<any>,
  id: string,
  pharmacyId: string,
  isLongTerm: boolean,
  pharmacistId: string,
  isBooking: boolean,
  details: IAdminWorkshiftDetails,
) => {
  const dispatch = useAppDispatch();
  const isFromCalendar = !id;
  const { isSelfService } = useSelfService();

  const professionalInfo = useAppSelector(getProfessionalMetaInfo);
  const pharmaciesInfo = useAppSelector(adminPharmaciesInfo);

  const { userMDSRate, userRate, pharmacyUserMDSRate, pharmacyUserRate } = useHourlyRates({ details, isFromCalendar });
  const replacementType = isFromCalendar ? formik.values.replacementType as ProfessionalReplacementTypes : details?.replacementType;

  const { values, setFieldValue } = formik ?? {};
  const { pharmacies, pharmacySelected, pharmacistSelected } = values ?? {};
  const {
    creditsLeft: professionalCreditsLeft,
    promoHourlyRate: hourlyRateBonus = 0,
  } = professionalInfo ?? {};
  const proCredits = isBooking ? 0 : professionalCreditsLeft;
  const {
    hourlyRate,
    promoHourlyRate,
    creditsLeft,
  } = pharmaciesInfo ?? {};

  const handlePharmacyRate = () => {
    setFieldValue('creditsLeft', creditsLeft);
    setFieldValue('promoHourlyRate', promoHourlyRate);
    if (isLongTerm) {
      const { weekday, weekend } = userMDSRate[
        formik.values.replacementType as keyof typeof userMDSRate
      ] ?? {};

      const { weekday: pharmacyWeekday, weekend: pharmacyWeekend } = pharmacyUserMDSRate[
        formik.values.replacementType as keyof typeof userMDSRate
      ] ?? {};

      setFieldValue(weekDayPaidHourlyRate, weekday);
      setFieldValue(weekendPaidHourlyRate, weekend);

      setFieldValue(
        'pharmacyWeekDayHourlyRate',
        pharmacyWeekday ?? 0,
      );
      setFieldValue(
        'pharmacyWeekendHourlyRate',
        pharmacyWeekend ?? 0,
      );

      return;
    }

    const userRateValue = userRate[formik.values.replacementType as keyof typeof userRate] ?? 0;
    const rate = proCredits ? userRateValue + hourlyRateBonus : userRateValue;

    const pharmacyRate = pharmacyUserRate[formik.values.replacementType as keyof typeof pharmacyUserRate] ?? 0;

    setFieldValue(
      'pharmacyHourlyRate',
      convertToDecimals(creditsLeft ? promoHourlyRate : pharmacyRate),
    );

    setFieldValue(
      'pharmacistHourlyRate',
      rate || convertToDecimals(hourlyRate),
    );
  };

  const handlePharmacistRate = () => {
    const rate = userRate[formik.values.replacementType as keyof typeof userRate];
    if (isLongTerm) {
      const { weekday, weekend } = userMDSRate[
        formik.values.replacementType as keyof typeof userMDSRate
      ] ?? {};

      setFieldValue(weekDayPaidHourlyRate, weekday);
      setFieldValue(weekendPaidHourlyRate, weekend);

      return;
    }

    setFieldValue(
      'pharmacistHourlyRate',
      rate || convertToDecimals(hourlyRate),
    );
  };

  useEffect(() => {
    if (pharmacyId || formik.values.pharmacyId) {
      dispatch(
        getPharmaciesInfo({ id: pharmacyId || formik.values.pharmacyId }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyId, formik.values.pharmacyId]);

  useEffect(() => {
    if ((pharmacistId || formik.values.pharmacistId) && !isSelfService) {
      dispatch(
        adminGetProfessionalInfo(pharmacistId || formik.values.pharmacistId),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacistId, formik.values.pharmacistId]);

  useComponentDidUpdate(() => {
    if (isFromCalendar) {
      handlePharmacyRate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hourlyRate,
    pharmacySelected,
    isLongTerm,
    userRate,
    promoHourlyRate,
    formik.values.replacementType,
    userMDSRate,
    creditsLeft,
  ]);

  useComponentDidUpdate(() => {
    if (isFromCalendar) {
      handlePharmacistRate();
    }
  }, [formik.values.replacementType]);

  useComponentDidUpdate(() => {
    if ((pharmacistId || formik.values.pharmacistId) && !isBooking) {
      const userRateValue = userRate[replacementType as keyof typeof userRate] ?? 0;
      const rate = professionalCreditsLeft
        ? userRateValue + hourlyRateBonus
        : userRateValue;
      formik.setFieldValue('pharmacistHourlyRate', rate);
      formik.setFieldValue('professionalCreditsLeft', professionalCreditsLeft);
      formik.setFieldValue('hourlyRateBonus', hourlyRateBonus);
    }
  }, [
    professionalCreditsLeft,
    hourlyRateBonus,
    pharmacistSelected,
    pharmacistId,
    replacementType,
  ]);

  useEffect(
    () =>
      () => {
        dispatch(adminResetValue(['professionalMetaInfo', 'pharmaciesInfo']));
      },
    [],
  );

  useEffect(() => {
    if (!pharmacies.trim() && !pharmacySelected && isFromCalendar) {
      setFieldValue('pharmacyHourlyRate', '');
      setFieldValue('pharmacistHourlyRate', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacies, pharmacySelected]);
};

export default useExpressBookingHr;
